import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RelativeTime } from './relative-time';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import type { PassportState, RobotPassport } from '@/schemas/passport';

interface RobotPassportProps {
  passport: RobotPassport;
}

const STATE_CLASS: Record<PassportState, string> = {
  awaiting_activation: 'text-status-pending',
  pending: 'text-status-pending',
  permitted: 'text-status-permitted',
  active: 'text-status-active',
  expired: 'text-status-expired',
  revoked: 'text-status-revoked',
};

function fieldLabel(t: TFunction, key: string): string {
  return t(`licenses.detail.passport.${key}`);
}

/**
 * Robot passport (#163) — the machine's model / serial / lifecycle state and the
 * source it was born from. The append-only "who changed what" trail lives in its
 * own [`PassportHistory`] section (paginated — the trail grows without bound, one
 * row per offline activation), so this header stays a compact summary.
 */
export function RobotPassport({ passport }: RobotPassportProps) {
  const { t } = useTranslation();

  return (
    <section data-testid="robot-passport">
      <div className="mb-5 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          {t('licenses.detail.passport.title')}
        </h2>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-5 border-y py-6 sm:grid-cols-4">
        <PassportField label={fieldLabel(t, 'model')} value={passport.birth?.model ?? '—'} />
        <PassportField label={fieldLabel(t, 'serial')} value={passport.serial} mono />
        <div>
          <dt className="text-[13px] text-faint">{fieldLabel(t, 'stateLabel')}</dt>
          <dd
            className={`mt-1 text-[15px] font-medium capitalize ${STATE_CLASS[passport.state]}`}
            data-testid="passport-state"
          >
            {t(`licenses.detail.passport.state.${passport.state}`)}
          </dd>
        </div>
        <PassportField
          label={fieldLabel(t, 'source')}
          value={passport.birth?.source ?? '—'}
          testId="passport-source"
        />
        <PassportField
          label={fieldLabel(t, 'registeredAt')}
          value={<RelativeTime date={passport.birth?.registeredAt ?? null} fallback="—" />}
        />
      </dl>
    </section>
  );
}

function PassportField({
  label,
  value,
  mono,
  testId,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  testId?: string;
}) {
  return (
    <div>
      <dt className="text-[13px] text-faint">{label}</dt>
      <dd
        className={`mt-1 text-[15px] text-foreground ${mono ? 'font-mono' : ''}`}
        data-testid={testId}
      >
        {value}
      </dd>
    </div>
  );
}
